import {
  useState, useMemo, useEffect, FormEvent, useCallback, useRef,
} from 'react';
import {
  ApiSpokenLanguagesArr,
  ApiSpokenLanguageLevelsObj,
  ApiStaticIdNameLevel,
} from '@type/v1-api-types/static';
import { SelectedLanguageParam } from '@type/filters';
import { useSwrSpokenLanguages, useSwrSpokenLanguagesLevels } from '@hooks/useSwrApi/endpoints/static';
import { useTranslation } from 'next-i18next';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { filterStrings } from '@utils/arrays/filter-strings';
import { arrayRemoveObjects } from '@utils/arrays/arrayRemoveItems';


/**
 * @description Custom hook for spoken languages combobox and language pills (name, level, remove). It's the logic for any
 * kind of spoken languages selector component like filters, user profile (cv edit), etc.
 *
 * @returns Object with the following properties:
 * - **comboboxOptions:** Combobox options object, so we don't have to pass the same options to the combobox component every time.
 * - **selectedLanguages:** Array of selected languages to be displayed as pills.
 * - **adjustedLanguageLevels:** Mutated API array with the 'all levels' option.
 * - **selectedLanguagesString:** String of selected languages to be used in a hidden input (or wherever needed) used for form posting.
 * - **selectedLanguagesFiltersLabel:** String of selected languages to be used for filters.
 * - **buildSelectedLanguagesFromString:** Function to build the selectedLanguages array from the hidden input value. If the form is updated programmatically,
 * we need a way to update the selected languages array and properly build the language pills.
 * - **addLanguage:** Function to add a selected language from the combobox to the selectedLanguages array. Add it only if it's not already in the selectedLanguages array.
 * - **changeLanguageLevel:** Function to mutate an existing language object inside the selectedLanguages array with the selected level.
 * - **deleteLanguage:** Function to delete a language from the selectedLanguages array.
 *
 * @example
 * // FOR MAIN COMPONENT
 * // **********************************************
 * const {
 *   comboboxOptions,
 *   addLanguage,
 *   selectedLanguages,
 *   adjustedLanguageLevels,
 *   selectedLanguagesString,
 *   buildSelectedLanguagesFromString,
 *   changeLanguageLevel,
 *   deleteLanguage,
 * } = useSpokenLanguagesBuilder();
 *
 * // The combobox component
 * <ComboBox
 *   placeholder={t('onboarding.step5.example.placeholder')}
 *   options={comboboxOptions}
 *   renderList={(item, index, listKey, getItemProps, listStyling) => (
 *     <li key={listKey} className={`${listStyling} cursor-pointer`} {...getItemProps({ item, index })}>
 *       {item['name' as keyof typeof item]}
 *     </li>
 *   )}
 *   onSelectedItemChange={(item) => {
 *     // Add the selected language to show as pill
 *     if (item) addLanguage(item as SelectedLanguageParam);
 *   }}
 *   {...recordFunc('languagesList', {
 *     removeFromSubmit: true,
 *   })}
 * />
 *
 * // The pills list
 * {
 *   selectedLanguages?.length > 0 && selectedLanguages?.map((language) => (
 *     <LanguagePill
 *       key={language.id}
 *       language={language}
 *       languageLevels={adjustedLanguageLevels}
 *       changeLanguageLevel={changeLanguageLevel}
 *       deleteLanguage={deleteLanguage}
 *     />
 *   ))
 * }
 *
 * // The hidden input to store the selected languages
 * <Input
 *   type="hidden"
 *   value={selectedLanguagesString}
 *   onInput={(e) => {
 *     // If the event is not trusted (e.isTrusted = false), it means it was triggered programmatically
 *     // (ie: bjForm's setFormValues() function)
 *     if (!e.isTrusted) buildSelectedLanguagesFromString(e);
 *   }}
 *   {...recordFunc(spokenLanguagesApiParameter)}
 * />
 *
 * // FOR LANGUAGE PILLS COMPONENT
 * // **********************************************
 * // change a language level
 * onClick={() => changeLanguageLevel(language.id, level.id, level.name)}
 *
 * // delete a language
 * onClick={() => deleteLanguage(language.id)}
 */
export const useSpokenLanguagesBuilder = () => {
  // Translation
  const { t } = useTranslation('common');

  // Router
  const { locale } = useExtendedRouter();

  // Initial input value
  const initialInputValue = useRef('');


  // Languages list from the API
  // **********************************************
  const {
    data: apiLanguagesList,
  } = useSwrSpokenLanguages<ApiSpokenLanguagesArr>(locale || 'ro', true);


  // Languages levels list from the API
  // **********************************************
  const {
    data: spokenLanguagesLevels,
  } = useSwrSpokenLanguagesLevels<ApiSpokenLanguageLevelsObj>(locale || 'ro', true);


  // Combine languages with levels
  const languagesWithLevels: ApiStaticIdNameLevel[] = apiLanguagesList.flatMap(
    (language) => spokenLanguagesLevels.map((level) => ({
      id: language.id,
      name: `${language.name} - ${level.name}`,
      levelShortname: level.name,
      level: level.id,
    })),
  );


  // Store the spoken languages selected by user from the combobox list to be displayed as pills and also to be sent to the API.
  // We will mutate the language objects to add the level inside the LanguagePill component
  // **********************************************
  const [
    selectedLanguages, setSelectedLanguages,
  ] = useState<SelectedLanguageParam[]>([]);


  // Adjusted language levels for 'all levels' option
  // **********************************************
  const adjustedLanguageLevels = useMemo(() => {
    const originalLevels = spokenLanguagesLevels || [];
    return [
      { id: '', name: t('placeholder.spoken_language.level') },
      ...originalLevels,
    ];
  }, [spokenLanguagesLevels, t]);


  // Convert the selectedLanguages array to a string to be used in the hidden input (or wherever needed)
  // **********************************************
  const selectedLanguagesString = useMemo(() => {
    let languagesString = '';

    selectedLanguages.forEach((language, index) => {
      const formattedLanguage = `${language.id}${language.level ? `_${language.level}` : ''}`;
      const commaSeparator = index < selectedLanguages.length - 1 ? ',' : '';
      languagesString += `${formattedLanguage}${commaSeparator}`;
    });

    return languagesString;
  }, [selectedLanguages]);


  // Convert the selectedLanguages array to a string to be used for filters
  // **********************************************
  const selectedLanguagesFiltersLabel = useMemo(() => {
    let languagesString = '';

    selectedLanguages.forEach((language, index) => {
      const formattedLanguage = `${language.name}${language.levelName ? ` - ${language.levelName}` : ''}`;
      const commaSeparator = index < selectedLanguages.length - 1 ? ', ' : '';
      languagesString += `${formattedLanguage}${commaSeparator}`;
    });

    return languagesString;
  }, [selectedLanguages]);


  // HELPER FUNCTIONS
  // *******************************************************************************************************************
  // Saved values set with setFormValues for later, when apiLanguagesList and spokenLanguagesLevels are not yet fetched
  // We will use these to rebuild the selectedLanguages array, then set it to undefined
  const [savedValues, setSavedValues] = useState<string | undefined>(undefined);


  // We need to be able to update the selected languages when updated programmatically from the outside
  // of component (ie: by setFormValues() function) or URL query params
  // When using this function, don't update the selected languages if the event is trusted (user input) or currently updating the languages
  // Programatic updates are always not trusted (event.isTrusted = false)
  // **********************************************
  const buildSelectedLanguagesFromString = useCallback((event: FormEvent<HTMLInputElement>) => {
    // Helper variables
    const inputValue = event.currentTarget.value;
    initialInputValue.current = inputValue;
    const languagesArray = inputValue.split(',');

    // If the apiLanguagesList and spokenLanguagesLevels are not yet fetched, save the values for later
    if (!apiLanguagesList.length || !spokenLanguagesLevels.length) {
      setSavedValues(inputValue);
    }

    // Build a new languages array from the hidden input value (values are like: en,fr_1,de_0...).
    if (apiLanguagesList.length && spokenLanguagesLevels.length) {
      const newLanguages: SelectedLanguageParam[] = languagesArray.map((language) => {
        const languageParts = language.split('_');
        const languageId = languageParts[0];
        const languageLevel = languageParts[1] || '';

        // Find the language in apiLanguagesList and levels in adjustedLanguageLevels
        const foundLanguage = apiLanguagesList.find((apiLanguage) => apiLanguage.id === languageId);
        const foundLevel = adjustedLanguageLevels.find((apiLanguage) => apiLanguage.id === languageLevel);

        // Build the language object
        return {
          id: foundLanguage?.id || '',
          name: foundLanguage?.name || '',
          level: foundLevel?.id || '',
          levelName: foundLevel?.name || '',
        };
      });

      // Update the selected languages state
      setSelectedLanguages(inputValue === '' ? [] : newLanguages);
    }
  }, [adjustedLanguageLevels, apiLanguagesList, spokenLanguagesLevels]);


  // Rebuild the selectedLanguages array
  // **********************************************
  useEffect(() => {
    // If not already available, rebuild the selectedLanguages array when the apiLanguagesList and spokenLanguagesLevels are fetched
    if (apiLanguagesList.length && spokenLanguagesLevels.length && savedValues) {
      buildSelectedLanguagesFromString({ currentTarget: { value: savedValues } } as FormEvent<HTMLInputElement>);
      setSavedValues(undefined);
    }
    // Rebuild on locale change (the apiLanguagesList and spokenLanguagesLevels will be re-fetched)
    if (apiLanguagesList.length && spokenLanguagesLevels.length) {
      buildSelectedLanguagesFromString({ currentTarget: { value: initialInputValue.current } } as FormEvent<HTMLInputElement>);
    }
  }, [apiLanguagesList, spokenLanguagesLevels, savedValues, buildSelectedLanguagesFromString]);


  // LANGUAGE PILLS HELPERS
  // *******************************************************************************************************************
  // Select language level
  // Mutate an existing language object inside the selectedLanguages array with the selected level
  // **********************************************
  const changeLanguageLevel = (languageId: string, languageLevelId: string, languageLevelName: string) => {
    setSelectedLanguages((current) => {
      const newLanguages = [...current];
      const languageIndex = newLanguages.findIndex((lang) => lang.id === languageId);
      newLanguages[languageIndex].level = languageLevelId || '';
      newLanguages[languageIndex].levelName = languageLevelName;
      return newLanguages;
    });
  };


  // Delete a language from the selectedLanguages array
  // **********************************************
  const deleteLanguage = (languageId: string) => {
    setSelectedLanguages((current) => {
      const newLanguages = [...current];
      return newLanguages.filter((lang) => lang.id !== languageId);
    });
  };


  // COMBOBOX HELPERS
  // *******************************************************************************************************************
  // Combobox filter languages from the list
  // **********************************************
  const filterObjectList = (inputValue: string | undefined) => filterStrings(inputValue, 'includes', ['name']);


  // Combobox options
  // **********************************************
  const comboboxOptions = {
    // Filter the languages that are already selected as pills.
    staticList: arrayRemoveObjects(apiLanguagesList, selectedLanguages, 'id'),
    forceRefreshStaticList: true,
    clearOnSelect: true,
    valueFromKey: 'name',
    // Pass a filtering function to the combobox so when the user types in the input, the list is filtered
    filteringFunction: filterObjectList,
    freeSolo: false,
    showResetButton: false,
  };


  // Combobox options with level included
  // **********************************************
  const comboboxOptionsWithLevels = {
    // Filter the languages that are already selected as pills.
    staticList: languagesWithLevels,
    forceRefreshStaticList: true,
    clearOnSelect: true,
    valueFromKey: 'name',
    // Pass a filtering function to the combobox so when the user types in the input, the list is filtered
    filteringFunction: filterObjectList,
    freeSolo: false,
    showResetButton: false,
  };


  // Add a selected language from the combobox to the selectedLanguages array
  // Add it only if it's not already in the selectedLanguages array
  // **********************************************
  const addLanguage = (selectedLanguage: SelectedLanguageParam) => {
    setSelectedLanguages((current) => {
      const newLanguages = [...current];
      const newLanguage = selectedLanguage;
      if (!newLanguages.find((language) => language.id === newLanguage.id)) {
        newLanguages.push({
          ...newLanguage,
          level: adjustedLanguageLevels[0].id,
          levelName: adjustedLanguageLevels[0].name,
        });
      }
      return newLanguages;
    });
  };


  // RETURN
  // *******************************************************************************************************************
  return {
    comboboxOptions,
    comboboxOptionsWithLevels,
    selectedLanguages,
    adjustedLanguageLevels,
    selectedLanguagesString,
    selectedLanguagesFiltersLabel,
    buildSelectedLanguagesFromString,
    addLanguage,
    changeLanguageLevel,
    deleteLanguage,
  };
};
